/**
 * Branding utilities
 * Applies environment-specific branding to the site
 */

/**
 * Apply branding configuration to the page
 */
export function applyBranding(config) {
  // Burger menu toggle is structural (not branding-driven), so wire it up
  // regardless of whether a valid config was passed.
  initNavbarBurger();

  if (!config || !config.branding) {
    console.warn('No branding configuration provided');
    return;
  }

  const { branding, footer } = config;

  // Update browser tab title. The navbar brand is a fixed "CooperScene"
  // (matching the CooperScene project page's navbar), not branding-driven.
  if (branding.siteName) {
    document.title = branding.siteName;
  }

  // Sentence under the site header/title
  if (branding.tagline) {
    fillPlaceholder('[data-branding="tagline"]', branding.tagline);
  }

  // Attribution 'stamp' badge in the header
  if (branding.stamp) {
    fillPlaceholder('[data-branding="stamp"]', branding.stamp);
  }

  // Apply color scheme
  if (branding.primaryColor) {
    document.documentElement.style.setProperty('--primary-color', branding.primaryColor);
  }
  if (branding.accentColor) {
    document.documentElement.style.setProperty('--accent-color', branding.accentColor);
  }

  // Handle logo
  if (branding.showLogo && branding.logoPath) {
    addLogo(branding.logoPath);
  }

  // Navigation tabs
  if (Array.isArray(branding.nav) && branding.nav.length > 0) {
    addNav(branding.nav);
  }

  // Update footer
  if (footer) {
    updateFooter(footer);
  }
}

/**
 * Wire up the navbar burger toggle (same behavior as the CooperScene
 * project page's static/js/main.js).
 */
function initNavbarBurger() {
  const burger = document.querySelector('.navbar-burger');
  const menu = document.querySelector('.navbar-menu');
  if (!burger || !menu) return;

  burger.addEventListener('click', () => {
    burger.classList.toggle('is-active');
    menu.classList.toggle('is-active');
  });
}

/**
 * Fill a hidden placeholder element with text and reveal it
 */
function fillPlaceholder(selector, text) {
  const element = document.querySelector(selector);
  if (element) {
    element.textContent = text;
    element.hidden = false;
  }
}

/**
 * Add logo to the navbar brand
 */
function addLogo(logoPath) {
  const brand = document.querySelector('.navbar-brand');
  if (!brand || brand.querySelector('.site-logo')) return;

  const logo = document.createElement('img');
  logo.src = logoPath;
  logo.alt = 'Logo';
  logo.className = 'site-logo';

  brand.insertBefore(logo, brand.firstChild);
}

/**
 * Add navigation links to the navbar's right-aligned nav-end group
 * (alongside any static items already there, e.g. the viewer page's
 * "Back to Scenes" link).
 */
function addNav(items) {
  const container = document.querySelector('.navbar-end');
  if (!container || container.dataset.navInjected) return;
  container.dataset.navInjected = 'true';

  // Compare by basename so links work when the site is served from a
  // subpath (e.g. data.ucr.edu/datasets/cooperscene/) with relative hrefs.
  const normalize = (path) => path.split('/').pop() || 'index.html';
  const currentPath = normalize(window.location.pathname);

  for (const item of items) {
    const link = document.createElement('a');
    link.className = 'navbar-item';
    link.href = item.href;
    link.textContent = item.label;
    if (normalize(item.href) === currentPath) {
      link.classList.add('is-active');
      link.setAttribute('aria-current', 'page');
    }
    container.appendChild(link);
  }
}

/**
 * Update footer content
 */
function updateFooter(footerConfig) {
  const footerElement = document.querySelector('[data-branding="footer"]');
  if (!footerElement) return;

  const container = document.createElement('div');
  container.className = 'container';

  // Institutional logo block
  if (footerConfig.logo && footerConfig.logo.path) {
    const brand = document.createElement('div');
    brand.className = 'footer-brand';

    const logoLink = document.createElement('a');
    logoLink.className = 'footer-logo-link';
    logoLink.href = footerConfig.logo.link || '#';
    logoLink.target = '_blank';
    logoLink.rel = 'noopener';

    const logo = document.createElement('img');
    logo.src = footerConfig.logo.path;
    logo.alt = footerConfig.logo.text || 'Institution logo';
    logo.className = 'footer-logo';
    logoLink.appendChild(logo);
    brand.appendChild(logoLink);

    if (footerConfig.logo.text) {
      const institution = document.createElement('p');
      institution.className = 'footer-institution';
      const institutionLink = document.createElement('a');
      institutionLink.href = footerConfig.logo.link || '#';
      institutionLink.target = '_blank';
      institutionLink.rel = 'noopener';
      institutionLink.textContent = footerConfig.logo.text;
      institution.appendChild(institutionLink);
      brand.appendChild(institution);
    }

    container.appendChild(brand);
  }

  // Attribution sentence
  if (footerConfig.attribution) {
    const attribution = document.createElement('p');
    attribution.className = 'footer-attribution';
    attribution.textContent = footerConfig.attribution;
    container.appendChild(attribution);
  }

  // Copyright / powered-by line
  const meta = document.createElement('p');
  meta.className = 'footer-meta';
  const parts = [];
  if (footerConfig.copyright) {
    parts.push(footerConfig.copyright);
  }
  parts.push('Powered by <a href="https://foxglove.dev" target="_blank" rel="noopener">Foxglove</a>');
  meta.innerHTML = parts.join(' &middot; ');
  container.appendChild(meta);

  footerElement.replaceChildren(container);
}
